import {
	ClinicSettings,
	Invoice,
	InvoiceDocumentType,
	InvoiceItem,
	InvoiceStatus,
	LocalPaymentMethod,
} from "../../../types/finance/FinanceTypes";
import {
	PublicInvoiceDto,
	PublicInvoiceLineDto,
} from "../../../types/finance/PublicInvoiceTypes";
import { PriceEntryMode, TaxCode } from "../../../utils/finance/gst";
import { dollarsToCents } from "../../../utils/finance/money";

/**
 * Map I6's patient-facing `PublicInvoiceDto` onto the `Invoice` + `ClinicSettings` shapes that the
 * SHARED `InvoiceDocument` (I8) consumes, so the public pay page renders the identical document a
 * staff member sees — without `InvoiceDocument` being rebuilt or made impure.
 *
 * ── Money ─────────────────────────────────────────────────────────────────────────────────────
 *
 * The DTO carries every amount as a fixed two-decimal STRING. `InvoiceDocument` reads the invoice
 * totals from those strings directly (subtotal / GST / total) and the paid/balance and per-line
 * figures from integer cents. `dollarsToCents` is a LOSSLESS conversion of an exact 2dp wire string
 * — it is the one parse direction the money module allows — so nothing is rounded twice.
 *
 * ── Per-line GST comes STRAIGHT from the DTO's stored value ────────────────────────────────────
 *
 * The DTO carries the snapshotted per-line GST as `line.gst` (I11). `InvoiceDocument` shows a
 * per-line GST column (from `item.lineTaxCents`) on a tax invoice, so we take the server's figure
 * verbatim — `dollarsToCents(line.gst)` — and never reconstruct it from the inclusive total and a
 * rate parsed out of the tax-code label. That reconstruction could be a cent off for an
 * exclusive-entry line at a rounding boundary; the stored value is what the legal document was
 * issued with and always wins. `lineEx` is the inclusive total minus that GST.
 */

function toItem(line: PublicInvoiceLineDto, index: number): InvoiceItem {
	const lineIncCents = dollarsToCents(line.lineTotal);
	// The stored per-line GST, taken verbatim — never recomputed from the label + inclusive total.
	const lineTaxCents = dollarsToCents(line.gst);
	const lineExCents = lineIncCents - lineTaxCents;

	return {
		id: String(index),
		description: line.description,
		quantity: line.quantity,
		sortOrder: index,
		// The DTO's `taxCode` is already a LABEL. `InvoiceDocument` looks it up in `TAX_CODE_LABELS`
		// and falls back to the raw value when it is not a known enum key — so the label passes
		// straight through. The cast is local to this boundary.
		taxCode: line.taxCode as unknown as TaxCode,
		// The snapshotted rate is not carried on the public DTO and `InvoiceDocument` never reads it
		// (it renders the label and the stored GST amount). Left 0 rather than parsed back out.
		taxRateBps: 0,
		taxComputed: true,
		unitPriceCents: dollarsToCents(line.unitPrice),
		lineExCents,
		lineTaxCents,
		lineIncCents,
		unitPrice: line.unitPrice,
		// `amount` mirrors lineEx historically and is never read by `InvoiceDocument`.
		amount: line.unitPrice,
	};
}

export interface PublicInvoiceDocumentModel {
	invoice: Invoice;
	settings: ClinicSettings;
}

export function publicInvoiceToInvoice(dto: PublicInvoiceDto): PublicInvoiceDocumentModel {
	const status = (dto.status as InvoiceStatus) ?? InvoiceStatus.SENT;

	const invoice: Invoice = {
		id: "",
		invoiceNumber: dto.invoiceNumber,
		// The DTO deliberately omits the user id; the patient's name (when present) is passed to
		// `InvoiceDocument` as a prop, so the "Patient <id>" fallback is never reached.
		userId: "",

		subtotalExCents: dollarsToCents(dto.totals.subtotalEx),
		taxCents: dollarsToCents(dto.totals.gst),
		totalIncCents: dollarsToCents(dto.totals.totalInc),
		amountPaidCents: dollarsToCents(dto.totals.amountPaid),
		balanceDueCents: dollarsToCents(dto.totals.balanceDue),

		// Invoice-level totals render from these authoritative strings — never reconstructed.
		subtotal: dto.totals.subtotalEx,
		tax: dto.totals.gst,
		total: dto.totals.totalInc,

		status,
		effectiveStatus: status,
		priceEntryMode: PriceEntryMode.INCLUSIVE,

		issueDate: dto.issueDate ?? "",
		dueDate: dto.dueDate ?? "",
		issuedAt: dto.issuedAt,

		collectViaStripe: dto.canPayOnline,
		stripeHostedInvoiceUrl: dto.stripeHostedInvoiceUrl,
		// PAYLINK/PAYID: carry the chosen SET so `InvoiceDocument` renders every selected+configured
		// surface identically to the staff drawer. The link and PayID themselves ride on the mapped
		// `settings` below.
		localPaymentMethods:
			(dto.localPaymentMethods as LocalPaymentMethod[] | undefined) ?? [
				LocalPaymentMethod.BANK_TRANSFER,
			],

		documentType: (dto.documentType as InvoiceDocumentType) ?? null,
		clinicAbn: dto.clinic.abn,
		clinicLegalName: dto.clinic.legalName,
		clinicAddress: dto.clinic.address,
		clinicContact: dto.clinic.contact,

		items: (dto.lines ?? []).map(toItem),
		createdAt: "",
		updatedAt: "",
		// Never rendered on the public page — `showInternalNotes` is false there.
		notes: null,
	};

	const settings: ClinicSettings = {
		id: "",
		legalName: dto.clinic.legalName ?? "",
		tradingName: dto.clinic.tradingName,
		abn: dto.clinic.abn,
		gstRegistered: dto.documentType === InvoiceDocumentType.TAX_INVOICE,
		bankAccountName: dto.clinic.bankAccountName,
		bankBsb: dto.clinic.bankBsb,
		bankAccountNumber: dto.clinic.bankAccountNumber,
		// PAYLINK/PAYID: the server already emits these LIVE and only for a selected+configured method
		// (null otherwise), so `InvoiceDocument`'s per-method render works off them directly. PayID is
		// split back into the two settings fields the shared component reads.
		invoicePaymentLink: dto.paymentLink,
		payIdType: dto.payId?.type ?? null,
		payId: dto.payId?.value ?? null,
		invoiceFooterText: dto.clinic.footerText,
		// Required by `ClinicSettings` but NEVER read by `InvoiceDocument` (it uses settings only for
		// bank details, the footer, and a draft-time identity fallback). Placeholder values only.
		invoiceNumberPrefix: "",
		invoiceNumberPadding: 0,
		defaultPaymentTermsDays: 0,
		defaultTaxCode: TaxCode.GST_FREE_HEALTH,
		defaultPriceEntryMode: PriceEntryMode.INCLUSIVE,
		payLinkTtlDays: 0,
	};

	return { invoice, settings };
}
